import { getIdTokenUsingQueryClient, queryKeys } from '@/data/queries'
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router'
import {
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import {
  UsersTable,
  useUsersColumns,
} from '@/components/dashboard/settings-page/UsersTable'
import { Button } from '@/components/ui/button'
import {
  useSuspenseQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { SearchIcon, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { LoadingSettings } from '@/components/loading-ui/LoadingSettings'
import { InviteUserDialog } from '@/components/dashboard/settings-page/InviteUserDialog'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import {
  usersOptions,
  userMeOptions,
  tenantProjectsOptions,
  orgQuotasOptions,
} from '@/data/queryOptions'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { DeleteUserConfirmationDialog } from '@/components/dashboard/settings-page/DeleteUserConfirmationDialog'
import { WorkspaceAccessDialog } from '@/components/dashboard/settings-page/WorkspaceAccessDialog'
import { deleteUser, mutationKeys } from '@/data/mutations'
import type { UserInfo, OrgQuota } from '@/data/entities'
import * as toast from '@/components/ui/toast'
import * as Sentry from '@sentry/react'
import { Progress } from '@/components/ui/progress'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

export const Route = createFileRoute(
  '/_authenticated/_settings/user/$userId/admin/user-management',
)({
  loader: async ({ context }) => {
    const { queryClient, authState } = context

    const TENANT_ID = useSelectedTenantStore.getState().selectedTenantId
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })

    return queryClient.ensureQueryData(usersOptions(TENANT_ID, idToken))
  },
  component: RouteComponent,
  pendingComponent: LoadingSettings,
})

function UserQuotaBar({ quota }: { quota: OrgQuota | undefined }) {
  const isMobile = useIsMobile()

  if (!quota) return null

  const isLimitReached = quota.current >= quota.limit
  const percentage = Math.min((quota.current / quota.limit) * 100, 100)

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        isMobile ? 'w-full' : 'w-[200px]',
      )}
    >
      <Progress
        value={percentage}
        className={cn(
          'h-2 flex-1',
          isLimitReached ? '[&>div]:bg-destructive' : '',
        )}
      />
      <span className="text-muted-foreground text-sm whitespace-nowrap">
        {quota.current}/{quota.limit} Users
      </span>
    </div>
  )
}

function RouteComponent() {
  const { authState, isOrgAdmin } = useRouteContext({
    from: '/_authenticated',
  })
  const { TENANT_ID, idToken } = useIdTokenAndTenantId()
  const isMobile = useIsMobile()
  const { data: users } = useSuspenseQuery(usersOptions(TENANT_ID!, idToken))
  const { data: projects } = useSuspenseQuery(
    tenantProjectsOptions(TENANT_ID!, idToken),
  )
  const { data: currentUser } = useSuspenseQuery(
    userMeOptions(TENANT_ID!, idToken),
  )
  const { data: quotas } = useQuery(orgQuotasOptions(TENANT_ID!, idToken))
  const [globalFilter, setGlobalFilter] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null)
  const [workspaceAccessDialogOpen, setWorkspaceAccessDialogOpen] =
    useState(false)
  const [userForWorkspaceAccess, setUserForWorkspaceAccess] =
    useState<UserInfo | null>(null)

  const membersQuota = quotas?.find((q) => q.name === 'max-members')
  const isLimitReached = membersQuota
    ? membersQuota.current >= membersQuota.limit
    : false

  const queryClient = useQueryClient()
  const deleteUserMutation = useMutation({
    mutationKey: mutationKeys.users.delete(TENANT_ID!),
    mutationFn: async (userId: string) => {
      await deleteUser({
        authState: authState!,
        tenantId: TENANT_ID!,
        userId,
        isOrgAdmin,
      })
    },
    onSuccess: () => {
      toast.success('User deleted successfully')
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.all(TENANT_ID!),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.org.quotas(TENANT_ID!),
      })
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    },
    onError: (error) => {
      Sentry.captureException(error)
      toast.error('Failed to delete user')
    },
  })

  const handleDeleteClick = (user: UserInfo) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.uid)
    }
  }

  const handleDeleteCancel = () => {
    setUserToDelete(null)
  }

  const handleWorkspaceAccessClick = (user: UserInfo) => {
    setUserForWorkspaceAccess(user)
    setWorkspaceAccessDialogOpen(true)
  }

  const table = useReactTable({
    data: users,
    columns: useUsersColumns({
      isOrgAdmin,
      onDeleteClick: handleDeleteClick,
      onWorkspaceAccessClick: handleWorkspaceAccessClick,
      projects,
      tenantId: TENANT_ID!,
    }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
    },
    initialState: {
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
    },
  })

  return (
    <div className="settings-container">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl">User Management</h1>
          <p className="text-copy-secondary text-sm">
            Manage users and their permissions.
          </p>
          {isMobile && <UserQuotaBar quota={membersQuota} />}
        </div>
        {!isMobile && <UserQuotaBar quota={membersQuota} />}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SearchIcon className="text-muted-foreground size-4" />
          <Input
            placeholder="Search users..."
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <div className="flex gap-2">
          {isLimitReached ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="warning" className="cursor-not-allowed">
                  <TriangleAlert className="mr-2 size-4" />
                  No Users Left
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-background text-foreground max-w-xs border p-3 shadow-lg">
                <p className="font-semibold">User Limit Reached</p>
                <p className="text-muted-foreground mt-1 text-sm">
                  You've reached the user limit for your current plan. Get in
                  touch with our team to upgrade and enable additional user
                  seats.
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <InviteUserDialog authState={authState!} tenantId={TENANT_ID!}>
              <Button>Invite New User</Button>
            </InviteUserDialog>
          )}
        </div>
      </div>
      <UsersTable table={table} />
      <DeleteUserConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        userName={userToDelete?.name}
        userEmail={userToDelete?.email}
        userPicture={userToDelete?.picture}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={deleteUserMutation.isPending}
      />
      {userForWorkspaceAccess && (
        <WorkspaceAccessDialog
          open={workspaceAccessDialogOpen}
          onOpenChange={setWorkspaceAccessDialogOpen}
          user={userForWorkspaceAccess}
          currentUser={currentUser}
          authState={authState!}
          tenantId={TENANT_ID!}
          idToken={idToken}
        />
      )}
    </div>
  )
}
